import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes, UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

import { devicesApi } from "./devicesApi"
import { performanceTestApi } from "./performanceTestApi"

export const clientApi = createApi({
  reducerPath: "clients",
  tagTypes: ["clients", "client", "archivedClients"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getClients: builder.query<ApiTypes.Res.Clients, UITypes.Params>({
      query: arg => ({
        url: `/clients`,
        params: prepareQueryParams(arg),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["clients"],
    }),
    getArchivedClients: builder.query<ApiTypes.Res.Clients, UITypes.Params>({
      query: arg => ({
        url: `/clients/archived`,
        params: prepareQueryParams(arg),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["archivedClients"],
    }),
    getClient: builder.query<{ data: ApiTypes.Model.ClientDevices }, number>({
      query: id => ({
        url: `/clients/${id}`,
        headers: { Accept: "application/json" },
      }),
      providesTags: ["client"],
    }),
    updateClient: builder.mutation<void, { body: ApiTypes.Req.ClientDevices; id: number }>({
      query: data => ({
        url: `/clients/${data?.id}`,
        body: data?.body,
        method: "PUT",
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["clients"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(devicesApi.util.invalidateTags(["devices"]))
          dispatch(performanceTestApi.util.invalidateTags(["PerformanceTestsList"]))
          dispatch(performanceTestApi.util.invalidateTags(["PerformanceTestsHistoryList"]))
        } catch (error) {
          console.error("Failed to update client:", error)
        }
      },
    }),
    archiveClient: builder.mutation<void, number>({
      query: id => ({
        url: `/clients/${id}/archive`,
        method: "PUT",
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["clients"],
    }),
    activateClient: builder.mutation<void, number>({
      query: id => ({
        url: `/clients/${id}/activate`,
        method: "PUT",
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["archivedClients"],
    }),
    transferDevice: builder.mutation<void, { sn: string; email: string }>({
      query: data => ({
        url: `/clients/transfer-device`,
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["clients", "client"],
    }),
    searchClientEmails: builder.query<{ data: string[] }, UITypes.Params>({
      query: arg => ({
        url: `/clients/search-client-emails`,
        params: prepareQueryParams(arg),
        headers: { Accept: "application/json" },
      }),
    }),
    getMobileUsageWidget: builder.query<
      { data: ApiTypes.Res.MobileUsageWidget },
      { date_start?: string; date_end?: string } | void
    >({
      query: params => ({
        url: `/clients/widgets/mobile-usage`,
        params: params ? prepareQueryParams(params) : undefined,
        headers: { Accept: "application/json" },
      }),
    }),
    getNewClientsTrendWidget: builder.query<{ data: ApiTypes.Res.NewClientsTrendWidget }, { period: number }>({
      query: params => ({
        url: `/clients/widgets/new-clients-trend`,
        params: prepareQueryParams(params),
        headers: { Accept: "application/json" },
      }),
    }),
  }),
})

export const {
  useLazyGetClientsQuery,
  useLazyGetArchivedClientsQuery,
  useLazyGetClientQuery,
  useUpdateClientMutation,
  useArchiveClientMutation,
  useActivateClientMutation,
  useTransferDeviceMutation,
  useLazySearchClientEmailsQuery,
  useLazyGetMobileUsageWidgetQuery,
  useLazyGetNewClientsTrendWidgetQuery,
} = clientApi

export const getClients = clientApi.endpoints.getClients
